import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { SxProps } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import {
    mdiMessageText,
    mdiBriefcaseVariant,
    mdiAccount,
    mdiEmail,
} from '@mdi/js';
import React from 'react';

import { IFeature } from 'graphql/types';
import { IGlobalDrawerType } from 'common/types';
import { useGlobalDrawer } from 'common/hooks/useGlobalDrawer';
import { useFeature } from 'common/hooks/useFeature';
import { Button } from 'common/components/Button';
import { Link } from 'common/components/Link';
import { Icon } from 'common/components/Icon';
import { useCanManageUser } from 'user/hooks/useCanManageUser';
import {
    IOfferEventEmailOptions,
    UserEmailDropdownMenu,
} from 'user/components/UserEmailDropdownMenu';
import { IUpdateGroupProps } from 'user/components/GroupUpdateDrawer';
import { IUserProfileProps } from 'user/components/UserProfileDrawer';

const userActionBarStyle: SxProps = {
    px: { xs: 2, sm: 4 },
    py: 2,
    borderTop: '1px solid',
    borderTopColor: 'divider',
    borderBottom: '1px solid',
    borderBottomColor: 'divider',
};

interface IProps {
    userId: string;
    children?: React.ReactNode;
    groupProps?: IUpdateGroupProps;
    hideChat?: boolean;
    hideEmail?: boolean;
    hidePortfolio?: boolean;
    hideProfile?: boolean;
    offerEventOptions?: IOfferEventEmailOptions;
    userProfileProps?: IUserProfileProps;
}

export const UserActionBar = ({
    userId,
    children,
    groupProps,
    hideChat,
    hideEmail,
    hidePortfolio,
    hideProfile,
    offerEventOptions,
    userProfileProps,
}: IProps) => {
    const [translate] = useTranslation();
    const { openGlobalDrawer } = useGlobalDrawer();
    const { canUse: canUsePortfolio } = useFeature(IFeature.Portfolio);
    const {
        canManageUser,
        isCurrentUser,
        loading: loadingCanManageUser,
    } = useCanManageUser(userId);
    const { canUse: canUseSkillsFeature } = useFeature(IFeature.Skill);
    const {
        showChat: canUseChat,
        showPortfolio: isPortfolioPublished,
        showSkills,
        loading: loadingFeatures,
    } = useFeature(undefined, true);
    const canUsePortfolioSkills = canUseSkillsFeature && showSkills;

    const loading = loadingCanManageUser || loadingFeatures;

    const showChat = !hideChat && !isCurrentUser && canUseChat;
    const showPortfolio =
        !hidePortfolio &&
        canUsePortfolio &&
        isPortfolioPublished &&
        (canManageUser || isCurrentUser);
    const showProfile = !hideProfile;
    const showEmail = !hideEmail && !isCurrentUser && canManageUser;

    const showBar =
        showChat || showPortfolio || showProfile || showEmail || !!children;

    if (loading || !showBar) return null;

    let portfolioButtonUrl = isCurrentUser
        ? 'PORTFOLIO_SKILLS'
        : 'USER_PORTFOLIO_SKILLS';

    if (!canUsePortfolioSkills) {
        portfolioButtonUrl = isCurrentUser
            ? 'PORTFOLIO_TRAININGS'
            : 'USER_TRAININGS';
    }

    return (
        <Box sx={userActionBarStyle}>
            <Grid container spacing={1}>
                {showChat && (
                    <Grid size={{ sm: 'auto', xs: 6 }} sx={{ display: 'flex' }}>
                        <Button
                            component={Link}
                            params={{ id: userId }}
                            startIcon={
                                <Icon path={mdiMessageText} size="1.9rem" />
                            }
                            sx={{ flex: 1 }}
                            to="USER_CHAT"
                            variant="outlined"
                        >
                            {translate('chat')}
                        </Button>
                    </Grid>
                )}
                {showPortfolio && (
                    <Grid size={{ sm: 'auto', xs: 6 }} sx={{ display: 'flex' }}>
                        <Button
                            component={Link}
                            params={
                                isCurrentUser
                                    ? undefined
                                    : {
                                          id: userId,
                                      }
                            }
                            startIcon={
                                <Icon
                                    path={mdiBriefcaseVariant}
                                    size="1.9rem"
                                />
                            }
                            sx={{ flex: 1 }}
                            to={portfolioButtonUrl}
                            variant="outlined"
                        >
                            {translate('portfolio')}
                        </Button>
                    </Grid>
                )}

                {showProfile && (
                    <Grid size={{ sm: 'auto', xs: 6 }} sx={{ display: 'flex' }}>
                        <Button
                            startIcon={<Icon path={mdiAccount} size="1.9rem" />}
                            sx={{ flex: 1 }}
                            variant="outlined"
                            onClick={() => {
                                openGlobalDrawer({
                                    type: IGlobalDrawerType.UserProfile,
                                    itemId: userId,
                                    props: {
                                        groupProps,
                                        onUpdate: userProfileProps?.onUpdate,
                                    },
                                });
                            }}
                        >
                            {translate('profile')}
                        </Button>
                    </Grid>
                )}

                {showEmail && (
                    <Grid size={{ sm: 'auto', xs: 6 }} sx={{ display: 'flex' }}>
                        <UserEmailDropdownMenu
                            anchorButton={
                                <Button
                                    startIcon={
                                        <Icon path={mdiEmail} size="1.9rem" />
                                    }
                                    sx={{ flex: 1 }}
                                    variant="outlined"
                                >
                                    {translate('email')}
                                </Button>
                            }
                            offerEventOptions={offerEventOptions}
                            userIds={[userId]}
                        />
                    </Grid>
                )}
                {children && (
                    <Grid
                        container
                        size={{ sm: 'auto', xs: 12 }}
                        sx={{ ml: { sm: 'auto' } }}
                    >
                        {React.Children.toArray(children).map((child, i) => (
                            <Grid
                                key={i}
                                size={{ sm: 'auto', xs: 6 }}
                                sx={{ display: 'flex' }}
                            >
                                {child}
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};
